import { useCallback, useEffect, useState } from "react";
import { apiFetchOrThrow } from "../api/client";
import { AnalyticsEvent, track } from "../lib/analytics";

export interface ConnectedAppGrant {
  id: string;
  clientId: string;
  clientName: string;
  scope: string[];
  grantedAt: string;
  lastUsedAt: string | null;
}

// Plain async helpers (testable without a React harness). The hooks below
// are thin wrappers that add loading state.

export async function fetchConnectedApps(): Promise<ConnectedAppGrant[]> {
  const data = await apiFetchOrThrow<{ grants: ConnectedAppGrant[] }>(
    "/oauth/grants",
  );
  return data.grants;
}

export async function revokeConnector(grantId: string): Promise<void> {
  await apiFetchOrThrow("/oauth/revoke", {
    method: "POST",
    body: JSON.stringify({ grantId }),
  });
}

export function useConnectedApps({ active = true }: { active?: boolean } = {}) {
  const [grants, setGrants] = useState<ConnectedAppGrant[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      setGrants(await fetchConnectedApps());
    } catch {
      // ignore — same pattern as useApiKeys
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) refetch();
  }, [active, refetch]);

  return { grants, loading, refetch };
}

export function useRevokeConnector() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (grantId: string) => {
    setIsPending(true);
    try {
      await revokeConnector(grantId);
      track(AnalyticsEvent.ConnectorRevoked);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
}
